(async function () {
    "use strict";

    const baseTitle = document.title;
    const baseUrl = window.location.origin + window.location.pathname;

    // Helper: Debounce function for input typing
    function debounce(func, wait = 300) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), wait);
        };
    }

    // Helper: Scroll target into view taking fixed header height into account
    function scrollToElement(target) {
        const header = document.querySelector("header .nav-container");
        if (!target) return;

        const headerHeight = header ? header.offsetHeight : 0;
        const targetRect = target.getBoundingClientRect().top;
        const offsetPosition = targetRect + window.scrollY - headerHeight - 100;

        window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: "smooth"
        });
    }

    class ReportFilterInstance {
        constructor(element) {
            this.container = element;
            this.page = 1;
            this.take = parseInt(this.container.dataset.take, 10) || 10;

            // UI Selectors
            this.elements = {
                topic: this.container.querySelector(".filter.topic select"),
                type: this.container.querySelector(".filter.type select"),
                subtype: this.container.querySelector(".filter.subtype select"),
                year: this.container.querySelector(".filter.year select"),
                sort: this.container.querySelector(".filter.sort select"),
                search: this.container.querySelector(".filter.search input"),
                list: this.container.querySelector(".list"),
                resultTemplate: this.container.querySelector(".result.template") || this.container.querySelector(".result"),
                titleFound: this.container.querySelector(".titlefound"),
                titleNotFound: this.container.querySelector(".titlenotfound"),
                numFirst: this.container.querySelector(".numfirst"),
                numTotal: this.container.querySelector(".numtotal"),
                numShowing: this.container.querySelector(".numshowing"),
                pagination: this.container.querySelector(".pagination-container"),
                alert: this.container.querySelector(".alert")
            };
        }

        getUrlParams() {
            return new URLSearchParams(window.location.search);
        }

        buildQueryString(pageValue) {
            const params = new URLSearchParams({
                page: pageValue,
                search: this.elements.search?.value.trim() || "",
                topic: this.elements.topic?.value || "",
                type: this.elements.type?.value || "",
                subtype: this.elements.subtype?.value || "",
                year: this.elements.year?.value || "",
                sort: this.elements.sort?.value || "",
                arp: this.container.dataset.arp || ""
            });
            return `?${params.toString()}`;
        }

        updateUrl(id) {
            let url = `${baseUrl}${this.buildQueryString(this.page)}`;
            if (id) {
                url += `&id=${encodeURIComponent(id)}`;
            } else {
                document.title = baseTitle;
            }
            window.history.pushState(null, baseTitle, url);
        }

        async init() {
            const defaultSubtype = this.container.getAttribute("data-defaultsubtype");
            const urlParams = this.getUrlParams();
            const clear = urlParams.get("clear") === "true";
            const shouldScroll = window.location.search.length > 0;

            await this.populateFilterSelections();
            await this.setSelectedFromUrl();

            if (defaultSubtype && !clear && this.elements.subtype) {
                this.elements.subtype.value = defaultSubtype;
            }

            this.addEvents();
            await this.populateResults(false);

            if (shouldScroll) {
                scrollToElement(this.container);
            }
        }

        addEvents() {
            const handleFilterChange = async () => {
                this.page = 1;
                await this.populateResults();
            };

            this.elements.topic?.addEventListener("change", handleFilterChange);
            this.elements.year?.addEventListener("change", handleFilterChange);
            this.elements.sort?.addEventListener("change", handleFilterChange);

            this.elements.type?.addEventListener("change", async () => {
                this.page = 1;
                const selectedOption = this.elements.type.options[this.elements.type.selectedIndex];
                const defaultSubtypeOnly = this.container.getAttribute("data-defaultsubtypeonly") === "True";
                const defaultSubtype = defaultSubtypeOnly ? this.container.getAttribute("data-defaultsubtype") : "";

                await this.populateSubTypes(selectedOption?.dataset.id, defaultSubtype);
                await this.populateResults();
            });

            this.elements.search?.addEventListener("input", debounce(handleFilterChange, 300));

            this.elements.subtype?.addEventListener("change", async () => {
                if (this.elements.subtype.dataset.ignorechange !== "true") {
                    this.page = 1;
                    await this.populateResults();
                }
            });
        }

        async setSelectedFromUrl() {
            const params = this.getUrlParams();
            const filterPage = parseInt(params.get("page"), 10);
            if (filterPage && Number.isInteger(filterPage) && filterPage > 0) {
                this.page = filterPage;
            }

            if (params.get("search") !== null) this.elements.search.value = params.get("search");
            if (params.get("topic") !== null) this.elements.topic.value = params.get("topic");
            if (params.get("type") !== null) this.elements.type.value = params.get("type");

            if (this.elements.type?.selectedIndex > 0) {
                const selectedOption = this.elements.type.options[this.elements.type.selectedIndex];
                await this.populateSubTypes(selectedOption.dataset.id, params.get("subtype"));
            }

            if (params.get("year") !== null) this.elements.year.value = params.get("year");
            if (params.get("sort") !== null) this.elements.sort.value = params.get("sort");
        }

        async populateResults(setBrowserUrl = true) {
            if (setBrowserUrl) {
                this.updateUrl();
            }

            // Clear existing results
            const existingResults = this.elements.list.querySelectorAll(".result:not(.template)");
            existingResults.forEach(el => el.remove());

            this.elements.titleFound?.classList.add("hidden");
            this.elements.titleNotFound?.classList.add("hidden");

            const skip = (this.page - 1) * this.take;
            const sortMap = { Oldest: 1, AToZ: 2, ZToA: 3 };
            const sortValue = sortMap[this.elements.sort.value] || 0;

            const postData = {
                criteria: {
                    Search: this.elements.search.value.trim(),
                    Topic: this.elements.topic.value,
                    Type: this.elements.type.value,
                    SubType: this.elements.subtype.value,
                    Year: this.elements.year.value ? parseInt(this.elements.year.value, 10) : null,
                    Sort: sortValue,
                    Skip: skip,
                    Take: this.take
                }
            };

            try {
                const response = await fetch("/umbraco/api/reports/getreportsusingdeliveryapi/", {
                    method: "POST",
                    cache: "no-store",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(postData)
                });

                if (!response.ok) throw new Error("Failed to fetch report results");
                const data = await response.json();
                console.log(data)
                this.populateListData(data, postData);
                this.populatePagination(data, skip);
                this.populateNumItems(data, skip);
            } catch (err) {
                console.error("Error populating report results:", err);
            }
        }

        populateNumItems(data, skip) {
            const total = data.total || 0;
            const firstItem = total === 0 ? 0 : skip + 1;
            const lastItem = Math.min(skip + this.take, total);
            const numShowing = total === 0 ? 0 : lastItem - firstItem + 1;

            if (this.elements.numFirst) this.elements.numFirst.textContent = firstItem;
            if (this.elements.numTotal) this.elements.numTotal.textContent = total;
            if (this.elements.numShowing) this.elements.numShowing.textContent = lastItem;

            if (numShowing > 0) {
                this.elements.titleFound?.classList.remove("hidden");
            } else {
                this.elements.titleNotFound?.classList.remove("hidden");
            }
        }

        populateListData(data, postData) {
            if (!data.items) return;

            data.items.forEach(item => {
                const itemElement = this.elements.resultTemplate.cloneNode(true);
                itemElement.classList.remove("template", "hidden");
                itemElement.dataset.id = item.id;

                if (postData.criteria.Type && postData.criteria.SubType && item.promote) {
                    itemElement.classList.add("promote");
                }

                const contentLink = itemElement.querySelector(".content");
                if (contentLink) {
                    contentLink.href = `${item.url}${this.buildQueryString(this.page)}`;
                }

                const typeSingular = item.repTypeSingular || item.repType;
                if (itemElement.querySelector(".reportType")) itemElement.querySelector(".reportType").textContent = typeSingular;
                if (itemElement.querySelector(".heading")) itemElement.querySelector(".heading").textContent = item.name;
                if (itemElement.querySelector(".date")) itemElement.querySelector(".date").textContent = `${this.container.dataset.published || ''} ${item.reportDate}`;
                if (itemElement.querySelector(".summary")) itemElement.querySelector(".summary").textContent = item.summary;

                this.populateTeaser(itemElement.querySelector(".teaser"), item.teaserImage);

                // Handle documents
                const docsEl = itemElement.querySelector(".docs");
                if (docsEl && item.documents?.length > 0) {
                    //const downloadText = docsEl.dataset.download || "Download";
                    item.documents.forEach(doc => {
                        if (!doc.size?.startsWith("-1")) {
                            docsEl.appendChild(this.createDocumentEl(doc));
                        }
                    });
                }

                // Attach Topic Click Filters
                itemElement.querySelectorAll(".topic").forEach(el => {
                    el.textContent = item.repTopic;
                    el.addEventListener("click", (e) => {
                        e.preventDefault();
                        this.resetSelections();
                        this.elements.topic.value = item.repTopic;
                        this.populateResults();
                    });
                });

                // Attach Type Click Filters
                itemElement.querySelectorAll(".type").forEach(el => {
                    el.textContent = item.repType;
                    el.addEventListener("click", (e) => {
                        e.preventDefault();
                        this.resetSelections();
                        this.elements.type.value = item.repType;
                        this.populateResults();
                    });
                });

                this.elements.list.appendChild(itemElement);
            });

            this.elements.list.classList.remove("hidden");
        }

        populateTeaser(teaserEl, teaserImage) {
            if (!teaserEl) return;
            teaserEl.replaceChildren();

            if (teaserImage?.url) {
                const img = document.createElement("img");
                img.src = `${teaserImage.url}`;
                img.alt = teaserImage.name || "";
                teaserEl.appendChild(img);
                teaserEl.classList.remove("hidden");
            } else {
                teaserEl.classList.add("hidden");
            }
        }

        createDocumentEl(doc) {
            const docLink = document.createElement("a");
            docLink.href = doc.url;
            docLink.className = "doc-link bl";
            docLink.title = doc.name;
            const downloadText = doc.linkName;
            docLink.textContent = `${downloadText} ${doc.documentType}, ${doc.size}`;
            docLink.target = "_blank";

            const container = document.createElement("div");
            container.className = "buttonlink";
            container.appendChild(docLink);
            return container;
        }

        resetSelections() {
            this.page = 1;
            if (this.elements.topic) this.elements.topic.value = "";
            if (this.elements.type) this.elements.type.value = "";
            if (this.elements.year) this.elements.year.value = "";
            if (this.elements.sort) this.elements.sort.value = "Newest";
            if (this.elements.search) this.elements.search.value = "";
        }

        populatePagination(data, skip) {
            if (!this.elements.pagination) return;

            this.elements.pagination.replaceChildren();
            const total = data.total || 0;
            const numPages = Math.ceil(total / this.take);
            const currentPage = Math.floor(skip / this.take) + 1;

            if (numPages > 0) {
                const ul = document.createElement("ul");
                ul.className = "pagination";

                for (let p = 1; p <= numPages; p++) {
                    const li = document.createElement("li");
                    if (p === currentPage) {
                        li.className = "active";
                        li.innerHTML = `<span>${p}</span>`;
                    } else {
                        const pageLink = document.createElement("a");
                        pageLink.textContent = p;
                        pageLink.href = this.buildQueryString(p);
                        pageLink.addEventListener("click", e => {
                            e.preventDefault();
                            this.page = p;
                            this.populateResults();
                            scrollToElement(this.container);
                        });
                        li.appendChild(pageLink);
                    }
                    ul.appendChild(li);
                }
                this.elements.pagination.appendChild(ul);
                this.elements.pagination.classList.remove("hidden");
            }

            if (this.elements.alert) {
                this.elements.alert.classList.toggle("hidden", skip <= total);
            }

            if (skip > total && total > 0) {
                window.location.href = baseUrl;
            }
        }

        async populateSubTypes(typeId, value) {
            if (!this.elements.subtype) return;

            const filterSubTypeContainer = this.container.querySelector(".filter.subtype");
            const defaultSubtype = this.container.getAttribute("data-defaultsubtype");
            const defaultSubtypeOnly = this.container.getAttribute("data-defaultsubtypeonly") === "True";

            let hasSubTypes = false;

            if (typeId) {
                try {
                    const res = await fetch(`/umbraco/api/reports/getsubtypes/${typeId}`, { cache: "no-store" });
                    const data = await res.json();

                    if (data?.length) {
                        hasSubTypes = true;
                        this.elements.subtype.removeAttribute("disabled");
                        this.elements.subtype.dataset.ignorechange = "true";
                        this.elements.subtype.replaceChildren();

                        if (!defaultSubtypeOnly) {
                            const optionAll = document.createElement("option");
                            optionAll.value = "";
                            optionAll.textContent = "All";
                            this.elements.subtype.appendChild(optionAll);
                        }

                        data.forEach(d => {
                            if (defaultSubtypeOnly && defaultSubtype && d !== defaultSubtype) return;

                            const option = document.createElement("option");
                            option.value = d;
                            option.textContent = d;
                            option.selected = d === value;
                            this.elements.subtype.appendChild(option);
                        });

                        this.elements.subtype.dataset.ignorechange = "false";
                    }
                } catch (err) {
                    console.error("Error fetching subtypes:", err);
                }
            }

            if (hasSubTypes) {
                filterSubTypeContainer?.classList.remove("nodisplay");
            } else {
                filterSubTypeContainer?.classList.add("nodisplay");
                this.elements.subtype.setAttribute("disabled", "true");
                const placeholder = this.elements.type.selectedIndex
                    ? this.container.dataset.subtypenone
                    : this.container.dataset.subtypeselecttype;

                this.elements.subtype.innerHTML = `<option value="">${placeholder || ''}</option>`;
            }

            if (defaultSubtype && defaultSubtypeOnly) {
                filterSubTypeContainer?.classList.add("nodisplay");
            }
        }

        async populateFilterSelections() {
            const filterId = this.container.getAttribute("data-id");
            if (!filterId) {
                console.error("Report filter element is missing data-id attribute.");
                return;
            }

            const defaultType = this.container.getAttribute("data-defaulttype");
            const defaultTypeOnly = this.container.getAttribute("data-defaulttypeonly") === "True";
            const defaultSubtypeOnly = this.container.getAttribute("data-defaultsubtypeonly") === "True";
            const defaultSubtype = defaultSubtypeOnly ? this.container.getAttribute("data-defaultsubtype") : "";

            try {
                const res = await fetch(`/umbraco/api/reports/getreporttypeset/${filterId}?defaultTypeId=${defaultType || ''}`, { cache: "no-store" });
                const data = await res.json();

                if (data.types?.length) {
                    let selectedIndex = 0;
                    data.types.forEach((type, idx) => {
                        if (type.default) selectedIndex = idx + 1;
                        if (defaultTypeOnly && !type.default) return;

                        const option = document.createElement("option");
                        option.value = type.name;
                        option.textContent = type.name;
                        option.dataset.id = type.id;
                        this.elements.type.appendChild(option);
                    });

                    this.elements.type.selectedIndex = selectedIndex;

                    if (defaultTypeOnly) {
                        this.elements.type.options[0]?.remove();
                        if (defaultType) {
                            this.elements.type.parentNode?.classList.add("nodisplay");
                            await this.populateSubTypes(defaultType, defaultSubtype);
                        }
                    }
                }

                if (data.topics?.length) {
                    data.topics.forEach(topic => {
                        const option = document.createElement("option");
                        option.value = topic;
                        option.textContent = topic;
                        this.elements.topic.appendChild(option);
                    });
                }

                if (data.years?.length) {
                    data.years.forEach(year => {
                        const option = document.createElement("option");
                        option.value = year;
                        option.textContent = year;
                        this.elements.year.appendChild(option);
                    });
                }
            } catch (err) {
                console.error("Error populating filter selections:", err);
            }
        }
    }

    // Initialize all report filter instances on DOM ready
    document.addEventListener("DOMContentLoaded", () => {
        const reportFilterElements = document.querySelectorAll(".reportFilter");
        reportFilterElements.forEach(el => {
            const filterInstance = new ReportFilterInstance(el);
            filterInstance.init();
        });
    });
})();